/**
 * Shared category-navigation model.
 *
 * The mega-menu (`DesktopNav`) and the category-page filter sidebar
 * (`ProductFilters`) must surface the *same* curated subcategories, so the
 * logic lives here once instead of being duplicated (and drifting) per view.
 *
 * Two kinds of top-level verticals exist in the live (scraped) catalogue:
 *
 *   • Jewelry / Bags / Accessories arrive with real child categories, so their
 *     submenus are data-driven — `curateChildren` sanitises + caps the tree.
 *   • Men / Women / Kids are each imported as a single flat category with no
 *     children, so they're sliced at the view layer via `CURATED_SUBMENUS`:
 *     every link lands on the REAL category page scoped by a keyword
 *     (`/category/<slug>?q=…&label=…`), giving the identical hero + filter +
 *     grid experience, and titling the page as the subcategory.
 *
 * The catalogue DB is owned and continuously written by the live VPS scraper,
 * so we sanitise at the view layer rather than mutating the source.
 */
import type { Category } from '@/types/product';

/** A navigation entry: a clean label and a ready-to-use route. */
export interface MenuLink {
  name: string;
  to: string;
}

// Navigation artefacts that leak in from the scraped taxonomy — never real
// subcategories, so they're dropped (matched on the cleaned, lower-cased name).
const JUNK_NAMES = new Set([
  'more',
  'shop by',
  'shop all',
  'customer stories',
  'customer reviews',
  'worry-free purchase',
  'gifts guide for him',
  'gifts guide for her',
  'wholesale jewelry china',
  'new arrivals',
  'best sellers',
]);

/**
 * Strip scrape noise from a category label:
 *   - glued product counts:  "Teardrop Earrings11k+ products" → "Teardrop Earrings"
 *   - trailing chevrons:      "Candy Bead Jewelry>"            → "Candy Bead Jewelry"
 *   - collapsed whitespace
 */
export function cleanCategoryName(raw: string): string {
  return (raw ?? '')
    .replace(/\s*\d[\d,]*\s*k?\s*\+?\s*products?\b.*$/i, '')
    .replace(/[>›»\-\s]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Sanitise + de-duplicate + cap a category's children.
 * Returns at most `limit` clean, distinct links in the source sort order
 * (which already surfaces the canonical top subcategories first).
 */
export function curateChildren(children: Category[], limit: number): MenuLink[] {
  const seen = new Set<string>();
  const out: MenuLink[] = [];

  for (const child of children) {
    if (child.isActive === false || !child.slug) continue;
    const name = cleanCategoryName(child.name);
    const key = name.toLowerCase();
    if (name.length < 2 || JUNK_NAMES.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push({ name, to: `/category/${child.slug}` });
    if (out.length >= limit) break;
  }

  return out;
}

/** A curated keyword refinement landing on the parent's real category page. */
const catLink = (slug: string, name: string, q: string): MenuLink => ({
  name,
  to: `/category/${slug}?q=${encodeURIComponent(q)}&label=${encodeURIComponent(name)}`,
});

/**
 * Curated subcategories for the apparel top-levels (Men / Women / Kids).
 * Each keyword is verified against live, *category-scoped* inventory (the
 * scraped taxonomy is leaky, so a global search count is not a reliable proxy).
 */
export const CURATED_SUBMENUS: Record<string, MenuLink[]> = {
  women: [
    catLink('women', 'Maxi Dresses', 'maxi'),
    catLink('women', 'Midi Dresses', 'midi'),
    catLink('women', 'Floral Dresses', 'floral'),
    catLink('women', 'Evening Dresses', 'evening'),
    catLink('women', 'Party Dresses', 'party'),
    catLink('women', 'Bodycon Dresses', 'bodycon'),
    catLink('women', 'Strap Dresses', 'strap'),
    catLink('women', 'Sleeveless Dresses', 'sleeveless'),
  ],
  men: [
    catLink('men', 'Hoodies', 'hoodie'),
    catLink('men', 'T-Shirts', 't-shirt'),
    catLink('men', 'Sweatshirts', 'sweatshirt'),
    catLink('men', 'Zip-Ups', 'zipper'),
    catLink('men', 'Long Sleeve', 'long sleeve'),
    catLink('men', 'Streetwear', 'streetwear'),
  ],
  kids: [
    catLink('kids', 'Boys', 'boys'),
    catLink('kids', 'Girls', 'girls'),
    catLink('kids', 'Clothing Sets', 'clothing set'),
    catLink('kids', 'Dresses', 'dress'),
    catLink('kids', 'Cartoon Prints', 'cartoon'),
  ],
};

/** A mega-menu curates; it never dumps the full tree. */
export const MAX_MENU_ITEMS = 16;

/**
 * Resolve the curated subcategory links for a top-level category — the single
 * source of truth shared by the mega-menu and the filter sidebar. Apparel
 * verticals use the hand-curated, search-backed menu; everything else is
 * data-driven from the live (scraped) category tree.
 */
export function getSubcategoryLinks(
  cat: Pick<Category, 'slug' | 'children'>,
  limit: number = MAX_MENU_ITEMS
): MenuLink[] {
  return CURATED_SUBMENUS[cat.slug] ?? curateChildren(cat.children ?? [], limit);
}

/**
 * Whether a `MenuLink.to` matches the current location. Compares pathname plus
 * the `q` keyword refinement, so `/category/men?q=hoodie&label=Hoodies` is
 * active iff the page is `men` AND scoped to `hoodie` (param order-independent,
 * and the display-only `label` is ignored).
 */
export function isMenuLinkActive(
  to: string,
  pathname: string,
  currentQ: string | null
): boolean {
  const [path, query = ''] = to.split('?');
  const linkQ = new URLSearchParams(query).get('q');
  return pathname === path && (linkQ ?? null) === (currentQ ?? null);
}
